// PAT10Arabic.js
// This component implements the Arabic version of the PAT-10 test following the same design pattern as SMFT30Arabic.js

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./PAT10.css";
import { BASE_URL } from "../../Config";
import logoImage from "../../../resources/1000028180.jpeg";
import { FiMenu } from 'react-icons/fi';

// Arabic questions organized by sections
const questions = {
  cognitive: [
    "كثير أفكر زيادة عن اللزوم في أدائي قبل المنافسة.",
    "أقلق أني ما أقدم أفضل ما عندي.",
    "أحس إني غير واثق من قدرتي على التعامل مع الضغط.",
    "أنشغل بالأفكار السلبية قبل المنافسة.",
    "أقلق من رأي الناس لو خسرت أو فشلت.",
    "أشك في تدريبي وتحضيري لما تقترب المنافسة.",
    "أفكر كثير في أخطائي السابقة قبل الحدث.",
    "أحس نفسي مضغوط ذهنيًا قبل المنافسات المهمة.",
    "ألقى صعوبة في التركيز بسبب القلق."
  ],
  somatic: [
    "يزيد نبض قلبي بشكل واضح قبل المنافسة.",
    "أحس بشد في عضلاتي قبل ما أبدأ المنافسة.",
    "أشعر رعشة في يدي أو رجلي قبل الأداء.",
    "أتنفس بسرعة أو أحس بضيق نفس لما أتوتر.",
    "أشعر مغص أو \"توتر في المعدة\" قبل المنافسة.",
    "أحس بدفقة أدرينالين تخليني مش مرتاح.",
    "ألاحظ تعرّق زائد قبل اللحظات اللي فيها ضغط عالي.",
    "أحس بعدم راحة جسدية وصعوبة إني أبقى ثابت قبل المنافسة.",
    "يجي إحساس بالذعر قبل بداية المنافسة مباشرة."
  ],
  selfConfidence: [
    "أؤمن بقدرتي على الأداء بشكل جيد تحت الضغط.",
    "أحافظ على ثقتي حتى لما أواجه خصوم أقوياء.",
    "أثق في تدريبي وتحضيري.",
    "أبقى إيجابي ومركز حتى في المواقف الصعبة.",
    "عندي ثقة أني أقدر أحقق أهدافي."
  ]
};

// Arabic answer labels (5-point scale)
const answerLabels = ["أعارض بشدة", "أعارض", "محايد", "أوافق", "أوافق بشدة"];

// Section configuration
const sections = [
  { key: 'cognitive_anxiety', name: 'القلق المعرفي', questions: questions.cognitive },
  { key: 'somatic_anxiety', name: 'القلق الجسدي', questions: questions.somatic },
  { key: 'confidence', name: 'الثقة بالنفس', questions: questions.selfConfidence }
];

export default function PAT10Arabic() {
  // State for user responses (section -> question index -> selected value)
  const [responses, setResponses] = useState({});
  const [showInstruction, setShowInstruction] = useState(true);
  const [currentSection, setCurrentSection] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Handle radio button selection for a question
  const handleSelect = (sectionKey, questionIndex, rating) => {
    setResponses(prev => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [questionIndex]: rating
      }
    }));
  };

  // Handle Cancel button (navigate back to dashboard)
  const handleCancel = () => {
    navigate("/dashboard");
  };

  // Handle Next Section button
  const handleNext = (e) => {
    e.preventDefault();
    const currentSectionData = sections[currentSection];
    const sectionResponses = responses[currentSectionData.key] || {};
    
    // Validate all questions in current section are answered
    for (let i = 0; i < currentSectionData.questions.length; i++) {
      if (!sectionResponses[i]) {
        setAlertMsg("يرجى الإجابة على جميع الأسئلة قبل المتابعة.");
        setShowAlert(true);
        return;
      }
    }
    
    setCurrentSection((prev) => prev + 1);
  };

  // Handle Submit button (on last section)
  const handleSubmit = async (e) => {
    e.preventDefault();
    const currentSectionData = sections[currentSection];
    const sectionResponses = responses[currentSectionData.key] || {};
    
    // Validate all questions in current section are answered
    for (let i = 0; i < currentSectionData.questions.length; i++) {
      if (!sectionResponses[i]) {
        setAlertMsg("يرجى الإجابة على جميع الأسئلة قبل المتابعة.");
        setShowAlert(true);
        return;
      }
    }
    
    setIsSubmitting(true);
    
    // Build grouped responses by section
    const groupedResponses = {};
    sections.forEach(section => {
      const sectionResponses = responses[section.key] || {};
      groupedResponses[section.key] = section.questions.map((_, index) => 
        sectionResponses[index] || 0
      );
    });
    
    // Prepare payload for API
    const payload = {
      test_type: "PAT-10-AR",
      responses: groupedResponses,
    };
    
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      console.log("PAT-10 Arabic - Submitting payload:", payload);
      console.log("PAT-10 Arabic - Token:", token);
      
      if (!token) {
        setAlertMsg("يرجى تسجيل الدخول أولاً.");
        setShowAlert(true);
        setIsSubmitting(false);
        setTimeout(() => navigate('/'), 2000);
        return;
      }

      const response = await fetch(`${BASE_URL}/api/test/submit_test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      console.log("PAT-10 Arabic - Response status:", response.status);
      console.log("PAT-10 Arabic - Response ok:", response.ok);

      if (!response.ok) {
        if (response.status === 401) {
          setAlertMsg("انتهت صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى.");
          setShowAlert(true);
          setIsSubmitting(false);
          setTimeout(() => navigate('/'), 2000);
          return;
        }
        throw new Error('Failed to submit test');
      }

      const responseData = await response.json();
      console.log("PAT-10 Arabic - Success response:", responseData);
      
      setAlertMsg("تم إرسال الاختبار بنجاح!");
      setShowAlert(true);
      setTimeout(() => {
        navigate('/test-summary');
      }, 1200);
    } catch (error) {
      setAlertMsg("حدث خطأ أثناء إرسال الاختبار. يرجى المحاولة مرة أخرى.");
      setShowAlert(true);
      setIsSubmitting(false);
    }
  };

  // Get current section data
  const currentSectionData = sections[currentSection];
  const isLastSection = currentSection === sections.length - 1;

  // Responsive helper
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600;

  return (
    <div
      style={
        isMobile
          ? {
              minHeight: '100vh',
              background: `linear-gradient(to top left, #2AA7A1 -25.98%, #E0F7F4 66.42%)`,
              fontFamily: 'Poppins, sans-serif',
            }
          : {
              minHeight: '100vh',
              background: `linear-gradient(to top left, #2AA7A1 -25.98%, #E0F7F4 66.42%)`,
              fontFamily: 'Poppins, sans-serif',
            }
      }
    >
      {/* Header with Logo */}
      <header className="dashboard-header" style={{ position: "static" }}>
        <div
          className="header-content"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            padding: "0 1rem",
            maxWidth: "1200px",
            margin: "0 auto",
          }}
        >
          <div className="header-logo-container">
            <img
              src={logoImage}
              alt="Power Mind Metrics Logo"
              style={{ height: "90px", width: "100px", marginRight: "10px" }}
            />
            <h1 className="header-logo" style={{ whiteSpace: isMobile ? 'nowrap' : undefined, fontSize: isMobile ? 22 : undefined }}>Power Mind Metrics</h1>
          </div>
          {isMobile ? (
            <div className="mobile-nav-menu">
              <button className="hamburger-btn" onClick={() => setMobileMenuOpen(v => !v)} style={{ background: 'none', border: 'none', padding: 0, marginLeft: 0, marginRight: 8 }}>
                <FiMenu size={28} />
              </button>
              {mobileMenuOpen && (
                <div className="mobile-nav-dropdown" style={{ position: 'absolute', top: 60, right: 10, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', zIndex: 1000, padding: 8 }}>
                  <button className="nav-link" style={{ display: 'block', width: '100%', textAlign: 'left', padding: 8 }} onClick={() => { setMobileMenuOpen(false); navigate('/profile'); }}>Profile</button>
                  <button className="nav-link" style={{ display: 'block', width: '100%', textAlign: 'left', padding: 8 }} onClick={() => { setMobileMenuOpen(false); navigate('/logout'); }}>Logout</button>
                </div>
              )}
            </div>
          ) : (
            <nav className="header-nav" style={{ marginLeft: "auto" }}>
              <button className="nav-link" onClick={() => navigate("/profile")}>Profile</button>
              <button className="nav-link" onClick={() => navigate("/logout")}>Logout</button>
            </nav>
          )}
        </div>
      </header>

      <div
        style={
          isMobile
            ? {
                minHeight: 'calc(100vh - 64px)',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                marginTop: 40,
                marginBottom: '110px',
                paddingBottom: '20px',
              }
            : {
                minHeight: 'calc(100vh - 64px)',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                marginTop: 40,
                marginBottom: 80,
                paddingBottom: '20px',
              }
        }
      >
        <div
          style={
            isMobile
              ? {
                  background: '#fff',
                  borderRadius: '1.2rem',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  width: '100vw',
                  maxWidth: '98vw',
                  minWidth: 0,
                  padding: '1.2rem 0.7rem 1.5rem 0.7rem',
                  boxSizing: 'border-box',
                  margin: 0,
                  marginBottom: '110px',
                  minHeight: '90vh',
                  direction: 'rtl',
                  fontFamily: 'Poppins, sans-serif',
                }
              : {
                  background: '#fff',
                  borderRadius: 8,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                  width: 1028,
                  minHeight: 970,
                  padding: '2rem',
                  direction: 'rtl',
                  fontFamily: 'Poppins, sans-serif',
                }
          }
        >
          {/* Test Title */}
          <h1 className="logo-title" style={{ color: ' rgba(52, 52, 52, 1)' }}>
            اختبار القلق والأداء الرياضي PAT-10 (Arabic)
          </h1>

          {/* Instruction Modal (shown at the start) */}
          {showInstruction ? (
            <div
              style={
                isMobile
                  ? {
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      width: '100vw',
                      height: '100vh',
                      background: 'rgba(52, 52, 52, 1)',
                      zIndex: 9999,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }
                  : {
                      position: 'fixed',
                      top: 0,
                      left: 0,
                      width: '100vw',
                      height: '100vh',
                      background: 'rgba(52, 52, 52, 1)',
                      zIndex: 9999,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }
              }
            >
              <div
                style={
                  isMobile
                    ? {
                        background: '#fff',
                        borderRadius: '1.2rem',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                        padding: '1.2rem 0.7rem 1.5rem 0.7rem',
                        minWidth: 0,
                        maxWidth: '98vw',
                        textAlign: 'right',
                        fontFamily: 'Poppins, sans-serif',
                        direction: 'rtl',
                      }
                    : {
                        background: '#fff',
                        borderRadius: 16,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                        padding: '2.5rem 2.5rem 2rem 2.5rem',
                        minWidth: 340,
                        maxWidth: 500,
                        textAlign: 'right',
                        fontFamily: 'Poppins, sans-serif',
                        direction: 'rtl',
                      }
                }
              >
                <h2 style={{ color: '#11998e', fontWeight: 700, fontSize: 24, marginBottom: 12, textAlign: 'center' }}>تعليمات الاختبار</h2>
                <div style={{ color: '#444', fontSize: 18, marginBottom: 18, whiteSpace: 'pre-line', lineHeight: 2 }}>
                  عزيزي اللاعب،<br />
                  شكرًا لك على تخصيص الوقت للمشاركة في اختبار القلق والأداء الرياضي.<br />
                  تهدف نتائج هذا الاختبار إلى إتاحة فرصة للتفكير الذاتي حول مختلف جوانب قلقك قبل المنافسة.<br />
                  الغرض من هذه النتائج هو مساعدتك على اكتساب فهم أعمق لحالتك الذهنية، وتعزيز إدراكك لنقاط قوتك والمجالات التي قد تحتاج إلى اهتمام. من المهم أن تعلم أن نتائج هذا الاختبار ليست أداة تشخيصية، وهدفها الأساسي هو تشجيع الوعي الذاتي.<br />
                  يرجى الأخذ في الاعتبار أن نتائج هذا الاختبار لا تُعد بديلاً عن أي تشخيص طبي، وإنما تُعد نقطة انطلاق للتفكير الذاتي.
                </div>
                <button
                  className="card-start-btn"
                  style={{ background: '#11998e', color: '#fff', fontWeight: 600, fontSize: 18, borderRadius: 8, marginTop: 12, width: '100%' }}
                  onClick={() => setShowInstruction(false)}
                >
                  متابعة
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Step-based Progress Bar */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 12,
                  width: '100%',
                  margin: '16px 0 8px 0',
                }}
              >
                {Array.from({ length: sections.length }).map((_, idx) => (
                  <div
                    key={idx}
                    style={{
                      flex: 1,
                      height: 10,
                      borderRadius: 6,
                      background: idx <= currentSection ? '#2AA7A1' : '#e0e0e0',
                      transition: 'background 0.3s',
                    }}
                  />
                ))}
              </div>
              {/* Section progress indicator */}
              <div
                className="progress-text"
                style={{ marginBottom: 16, color: 'rgba(52, 52, 52, 1)' }}
              >
                القسم {currentSection + 1} من {sections.length}
              </div>
              {/* Section Title */}
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 22,
                  color: '#11998e',
                  textAlign: 'center',
                  marginBottom: 8,
                }}
              >
                {currentSectionData.name}
              </div>

              {/* Questions */}
              {currentSectionData.questions.map((question, questionIndex) => (
                <div
                  key={questionIndex}
                  style={
                    isMobile
                      ? {
                          background: '#fff',
                          borderRadius: '1.2rem',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                          marginBottom: 12,
                          padding: '1.2rem 0.7rem 1.5rem 0.7rem',
                          boxSizing: 'border-box',
                        }
                      : {
                          background: '#fff',
                          borderRadius: 10,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                          marginBottom: 12,
                          padding: '15px 20px',
                        }
                  }
                >
                  <div className="question-text">
                    {questionIndex + 1}. {question}
                  </div>
                  
                  {/* Options: desktop = row, mobile = pairs of two */}
                  {isMobile ? (
                    (() => {
                      const pairs = [[1,2],[3,4],[5]];
                      return pairs.map((pair, rowIdx) => (
                        <div key={rowIdx} className="mtt20-options-pair-row">
                          {pair.map((value) => (
                            <label key={value} className="mtt20-option-label">
                              <input
                                type="radio"
                                name={`q${questionIndex}`}
                                value={value}
                                checked={responses[currentSectionData.key]?.[questionIndex] === value}
                                onChange={() => handleSelect(currentSectionData.key, questionIndex, value)}
                                className="mtt20-radio"
                              />
                              <span className="mtt20-option-label-text">{answerLabels[value-1]}</span>
                            </label>
                          ))}
                        </div>
                      ));
                    })()
                  ) : (
                    <div
                      className="options"
                      style={{
                        flexDirection: 'row',
                        gap: 24,
                        justifyContent: 'start',
                        border: 'none',
                        background: 'none',
                      }}
                    >
                      {answerLabels.map((label, i) => (
                        <div key={i}>
                          <label
                            className="option-label"
                            style={{ fontSize: 16, fontWeight: 500 }}
                          >
                            <input
                              type="radio"
                              name={`q${questionIndex}`}
                              value={i + 1}
                              checked={responses[currentSectionData.key]?.[questionIndex] === i + 1}
                              onChange={() => handleSelect(currentSectionData.key, questionIndex, i + 1)}
                              style={{
                                width: 18,
                                height: 18,
                                accentColor: '#11998e',
                                marginLeft: 6,
                              }}
                            />
                            {label}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Navigation Buttons */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                <button
                  onClick={handleCancel}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: '2px solid #11998e',
                    background: '#fff',
                    color: '#11998e',
                    fontWeight: '600',
                    fontSize: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    minWidth: '120px'
                  }}
                >
                  إلغاء
                </button>
                
                <button
                  onClick={isLastSection ? handleSubmit : handleNext}
                  disabled={isSubmitting}
                  style={{
                    padding: '12px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    background: isSubmitting ? '#94a3b8' : '#11998e',
                    color: '#fff',
                    fontWeight: '600',
                    fontSize: '16px',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    minWidth: '120px'
                  }}
                >
                  {isSubmitting ? 'جاري الإرسال...' : (isLastSection ? 'إرسال' : 'التالي')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Alert Modal */}
      {showAlert && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.5)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            padding: '30px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center',
            fontFamily: 'Cairo, Poppins, sans-serif',
            direction: 'rtl'
          }}>
            <h3 style={{ 
              color: '#11998e', 
              fontWeight: '700', 
              fontSize: '20px', 
              marginBottom: '15px' 
            }}>
              تنبيه
            </h3>
            <p style={{ 
              color: '#444', 
              fontSize: '16px', 
              marginBottom: '20px',
              lineHeight: '1.6'
            }}>
              {alertMsg}
            </p>
            <button
              onClick={() => setShowAlert(false)}
              style={{
                background: '#11998e',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              موافق
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
        padding: '12px 0',
        textAlign: 'center',
        fontFamily: 'Poppins, sans-serif',
        zIndex: 100
      }}>
        <p style={{
          margin: 0,
          fontSize: '14px',
          color: 'rgba(75, 75, 75, 0.8)',
          fontWeight: 400
        }}>
          © 2025 All rights reserved Power Mind Metrics
        </p>
      </div>
    </div>
  );
}